import { useEffect, useState } from 'react';

import { OneTrust } from '@24i/nxg-sdk-smartott-shared/src/utils/oneTrust';
import { GoogleTagManagerScriptProps } from './types';

const GTM_URL = 'https://www.googletagmanager.com/gtm.js';

const DEFAULT_DATA_LAYER = 'dataLayer';

type GTMWindow = Window & Record<string, unknown>;

const ensureDataLayer = (layerName: string): Array<Record<string, unknown>> => {
    const gtmWindow = window as unknown as GTMWindow;
    const currentLayer = gtmWindow[layerName];
    if (Array.isArray(currentLayer)) {
        return currentLayer as Array<Record<string, unknown>>;
    }

    const createdLayer: Array<Record<string, unknown>> = [];
    gtmWindow[layerName] = createdLayer;
    return createdLayer;
};

export default function GoogleTagManagerScript({
    containerId,
    enabled = true,
    dataLayerName = DEFAULT_DATA_LAYER,
}: GoogleTagManagerScriptProps) {
    const [shouldLoad, setShouldLoad] = useState<boolean>(false);

    useEffect(() => {
        let mounted = true;

        const checkConsent = async () => {
            if (!enabled || !containerId) {
                if (mounted) setShouldLoad(false);
                return;
            }

            try {
                const consent = await OneTrust?.getConsentStatus();
                // If consent explicitly disallows tracking, do not load GTM
                const disallowed =
                    consent &&
                    (consent.doNotTrack === true || consent.limitAdvertisementTracking === true);
                if (mounted) setShouldLoad(!disallowed);
            } catch (e) {
                // On error, default to loading the script (fallback behaviour)
                if (mounted) setShouldLoad(true);
            }
        };

        const recheckConsent = () => {
            checkConsent();
        };

        const unwatchConsent = OneTrust?.watchConsentStatus?.(recheckConsent);

        recheckConsent();

        return () => {
            mounted = false;
            unwatchConsent?.();
        };
    }, [containerId, enabled, dataLayerName]);

    useEffect(() => {
        if (!shouldLoad || !containerId) return;
        if (typeof window === 'undefined' || typeof document === 'undefined') return;

        const scriptId = `nxg-gtm-${containerId}`;
        if (document.getElementById(scriptId)) return;

        const dataLayer = ensureDataLayer(dataLayerName);
        dataLayer.push({
            'gtm.start': Date.now(),
            event: 'gtm.js',
        });

        const script = document.createElement('script');
        script.id = scriptId;
        script.async = true;
        script.src = `${GTM_URL}?id=${encodeURIComponent(containerId)}&l=${encodeURIComponent(
            dataLayerName
        )}`;

        const firstScript = document.getElementsByTagName('script')[0];
        if (firstScript?.parentNode) {
            firstScript.parentNode.insertBefore(script, firstScript);
            return;
        }

        document.head?.appendChild(script);
    }, [containerId, dataLayerName, shouldLoad]);

    return null;
}
